<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useTimeAgo } from 'dashboard/composables/useTimeAgo';
import countries from 'shared/constants/countries.js';
import {
  DuplicateContactException,
  ExceptionWithMessage,
} from 'shared/helpers/CustomErrors';

import Button from 'dashboard/components-next/button/Button.vue';
import Accordion from 'dashboard/components-next/Accordion/Accordion.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import InlineInput from 'dashboard/components-next/inline-input/InlineInput.vue';
import PhoneNumberInput from 'dashboard/components-next/phonenumberinput/PhoneNumberInput.vue';
import ContactLabels from 'dashboard/components-next/Contacts/ContactLabels/ContactLabels.vue';
import ConfirmContactDeleteDialog from 'dashboard/components-next/Contacts/ContactsForm/ConfirmContactDeleteDialog.vue';
import Policy from 'dashboard/components/policy.vue';

const props = defineProps({
  selectedContact: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['goToContactsList']);

const { t } = useI18n();
const store = useStore();
const { timeAgo } = useTimeAgo();

const confirmDeleteContactDialogRef = ref(null);
const editingKey = ref(null);
const editValue = ref('');

const SOCIAL_KEYS = [
  ['facebook', 'Facebook'],
  ['instagram', 'Instagram'],
  ['linkedin', 'LinkedIn'],
  ['twitter', 'X (Twitter)'],
  ['telegram', 'Telegram'],
  ['github', 'GitHub'],
  ['tiktok', 'TikTok'],
];

const additionalAttributes = computed(
  () => props.selectedContact?.additionalAttributes || {}
);

const countryOptions = computed(() =>
  countries.map(({ name, id }) => ({ label: name, value: id }))
);

const createdAt = computed(
  () => timeAgo(props.selectedContact?.createdAt) || '-'
);

const lastActivityAt = computed(
  () => timeAgo(props.selectedContact?.lastActivityAt) || '-'
);

const fields = computed(() => {
  const attrs = additionalAttributes.value;
  const social = attrs.socialProfiles || {};
  return [
    {
      key: 'name',
      label: t('CONTACTS_LAYOUT.DETAILS.FIELDS.NAME'),
      value: props.selectedContact?.name,
    },
    {
      key: 'email',
      label: t('CONTACTS_LAYOUT.DETAILS.FIELDS.EMAIL'),
      value: props.selectedContact?.email,
    },
    {
      key: 'phoneNumber',
      label: t('CONTACTS_LAYOUT.DETAILS.FIELDS.PHONE'),
      value: props.selectedContact?.phoneNumber,
      type: 'phone',
    },
    {
      key: 'companyName',
      label: t('CONTACTS_LAYOUT.DETAILS.FIELDS.COMPANY'),
      value: attrs.companyName,
    },
    {
      key: 'city',
      label: t('CONTACTS_LAYOUT.DETAILS.FIELDS.CITY'),
      value: attrs.city,
    },
    {
      key: 'country',
      label: t('CONTACTS_LAYOUT.DETAILS.FIELDS.COUNTRY'),
      value: attrs.country,
      editValue: attrs.countryCode,
      type: 'select',
    },
    {
      key: 'description',
      label: t('CONTACTS_LAYOUT.DETAILS.FIELDS.BIO'),
      value: attrs.description,
    },
    ...SOCIAL_KEYS.map(([key, label]) => ({
      key: `social.${key}`,
      label,
      value: social[key],
    })),
  ];
});

const startEdit = field => {
  editingKey.value = field.key;
  editValue.value = field.editValue ?? field.value ?? '';
};

const cancelEdit = () => {
  editingKey.value = null;
};

const updateContactField = async attrs => {
  try {
    await store.dispatch('contacts/update', {
      id: props.selectedContact.id,
      ...attrs,
    });
    await store.dispatch(
      'contacts/fetchContactableInbox',
      props.selectedContact.id
    );
    useAlert(t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.SUCCESS_MESSAGE'));
  } catch (error) {
    if (error instanceof DuplicateContactException) {
      if (error.data?.includes('email')) {
        useAlert(
          t(
            'CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.FORM.EMAIL_ADDRESS.DUPLICATE'
          )
        );
      } else if (error.data?.includes('phone_number')) {
        useAlert(
          t(
            'CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.FORM.PHONE_NUMBER.DUPLICATE'
          )
        );
      } else {
        useAlert(
          t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.FORM.ERROR_MESSAGE')
        );
      }
    } else if (error instanceof ExceptionWithMessage) {
      useAlert(error.data);
    } else {
      useAlert(t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.FORM.ERROR_MESSAGE'));
    }
  }
};

const saveField = (field, rawValue) => {
  const value = typeof rawValue === 'string' ? rawValue.trim() : rawValue;
  const attrs = additionalAttributes.value;

  if (field.key === 'name') {
    if (value && value !== props.selectedContact?.name) {
      updateContactField({ name: value });
    }
    return;
  }
  if (field.key === 'email' || field.key === 'phoneNumber') {
    updateContactField({ [field.key]: value });
    return;
  }
  if (field.key === 'country') {
    const selected = countries.find(option => option.id === value);
    updateContactField({
      additionalAttributes: {
        ...attrs,
        countryCode: value,
        country: selected?.name || '',
      },
    });
    return;
  }
  if (field.key.startsWith('social.')) {
    const platform = field.key.split('.')[1];
    updateContactField({
      additionalAttributes: {
        ...attrs,
        socialProfiles: { ...(attrs.socialProfiles || {}), [platform]: value },
      },
    });
    return;
  }
  updateContactField({
    additionalAttributes: { ...attrs, [field.key]: value },
  });
};

const commitEdit = field => {
  editingKey.value = null;
  saveField(field, editValue.value);
};

const onPhoneFocusOut = (field, event) => {
  if (editingKey.value !== field.key) return;
  if (event.currentTarget.contains(event.relatedTarget)) return;
  commitEdit(field);
};

const saveSelect = (field, value) => {
  editingKey.value = null;
  saveField(field, value);
};

const openConfirmDeleteContactDialog = () => {
  confirmDeleteContactDialogRef.value?.dialogRef.open();
};
</script>

<template>
  <div class="flex flex-col w-full gap-4 pb-6">
    <Accordion
      :title="t('CONTACTS_LAYOUT.DETAILS.SECTIONS.CONTACT_DETAILS')"
      is-open
      class="!border-0"
    >
      <div class="flex flex-col w-full gap-3">
        <div
          v-for="field in fields"
          :key="field.key"
          class="flex items-start gap-2 group/field"
        >
          <span class="w-24 pt-1 text-sm shrink-0 text-n-slate-11">
            {{ field.label }}
          </span>
          <div class="flex-1 min-w-0">
            <ComboBox
              v-if="editingKey === field.key && field.type === 'select'"
              :model-value="editValue"
              :options="countryOptions"
              @update:model-value="value => saveSelect(field, value)"
            />
            <div
              v-else-if="editingKey === field.key && field.type === 'phone'"
              @focusout="onPhoneFocusOut(field, $event)"
              @keydown.enter.prevent="commitEdit(field)"
              @keydown.escape.prevent="cancelEdit"
            >
              <PhoneNumberInput v-model="editValue" />
            </div>
            <InlineInput
              v-else-if="editingKey === field.key"
              v-model="editValue"
              focus-on-mount
              @enter-press="commitEdit(field)"
              @escape-press="cancelEdit"
              @blur="commitEdit(field)"
            />
            <div v-else class="flex items-start gap-1">
              <span class="flex-1 text-sm break-words text-n-slate-12">
                {{ field.value || '-' }}
              </span>
              <Button
                ghost
                xs
                slate
                icon="i-lucide-pencil"
                class="transition-opacity opacity-0 shrink-0 group-hover/field:opacity-100"
                @click="startEdit(field)"
              />
            </div>
          </div>
        </div>

        <div class="flex items-start gap-2">
          <span class="w-24 text-sm shrink-0 text-n-slate-11">
            {{ t('CONTACTS_LAYOUT.DETAILS.FIELDS.CREATED_AT') }}
          </span>
          <span class="flex-1 text-sm text-n-slate-12">{{ createdAt }}</span>
        </div>
        <div class="flex items-start gap-2">
          <span class="w-24 text-sm shrink-0 text-n-slate-11">
            {{ t('CONTACTS_LAYOUT.DETAILS.FIELDS.LAST_ACTIVITY') }}
          </span>
          <span class="flex-1 text-sm text-n-slate-12">{{
            lastActivityAt
          }}</span>
        </div>
      </div>
    </Accordion>

    <div class="-mx-4 border-t border-n-strong" />

    <Accordion
      :title="t('CONTACTS_LAYOUT.DETAILS.SECTIONS.TAGS')"
      is-open
      class="!border-0"
    >
      <ContactLabels :contact-id="selectedContact?.id" />
    </Accordion>

    <Policy :permissions="['administrator']">
      <div class="-mx-4 mb-6 border-t border-n-strong" />
      <div class="flex flex-col items-start w-full gap-4">
        <div class="flex flex-col gap-2">
          <h6 class="text-base font-medium text-n-slate-12">
            {{ t('CONTACTS_LAYOUT.DETAILS.DELETE_CONTACT') }}
          </h6>
          <span class="text-sm text-n-slate-11">
            {{ t('CONTACTS_LAYOUT.DETAILS.DELETE_CONTACT_DESCRIPTION') }}
          </span>
        </div>
        <Button
          :label="t('CONTACTS_LAYOUT.DETAILS.DELETE_CONTACT')"
          color="ruby"
          @click="openConfirmDeleteContactDialog"
        />
      </div>
      <ConfirmContactDeleteDialog
        ref="confirmDeleteContactDialogRef"
        :selected-contact="selectedContact"
        @go-to-contacts-list="emit('goToContactsList')"
      />
    </Policy>
  </div>
</template>
